// --- React
import React, { useCallback } from 'react';
// --- UI Components
import Typography from '@dtci/spellbook/Typography';
// --- Hooks
import { useDialogsActions } from '../hooks';
import { useDialogsForm } from '../forms';

/** * LinkedProductsForm Component
 *
 * This component is responsible for rendering the link/unlink confirmation message.
 * It displays a message to confirm the link/unlink action.
 *
 * @component
 * @param {Object} msg - The message object containing confirmation text.
 * @returns {JSX.Element} The rendered LinkedProductsForm component.
 */
const LinkedProductsForm = ({ msg }) => (
    <div className="flexBox">
        <Typography variant="T30">{msg[0]}</Typography>
        <Typography variant="T20">{msg[1]}</Typography>
    </div>
);

/**
 * useLinkedProductsDialog Hook
 *
 * This hook provides functionality to open a link/unlink confirmation dialog.
 * It uses the useDialogsActions and useDialogsForm hooks to manage the dialog state.
 * It also provides a handleSubmit function to handle the form submission.
 *
 * @returns {Object} An object containing the openLinkedProductsDialog function.
 */
export const useLinkedProductsDialog = () => {
    const { openDialog, closeDialog } = useDialogsActions();
    const form = useDialogsForm();

    // Function to handle form submission
    const handleSubmit = useCallback(
        (onSubmit) => {
            onSubmit(form.state.formData);
            form.reset();
            closeDialog();
        },
        [form, closeDialog],
    );

    // Function to open the delete confirmation dialog
    const openLinkedProductsDialog = useCallback(
        (title, label, msg, onConfirm) => {
            openDialog({
                title: title,
                content: <LinkedProductsForm msg={msg} />,
                closeAction: true,
                primaryAction: {
                    label,
                    action: () => handleSubmit(onConfirm),
                },
            });
        },
        [openDialog, handleSubmit],
    );

    return { openLinkedProductsDialog };
};
